import React, { useState, Fragment } from "react";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { getFileIconImage } from "../../../../utils/fileUtils";
import {
  EastRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  PublicRounded,
} from "@mui/icons-material";

const useStyles = makeStyles()((theme) => ({
  citationsGroup: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.light,
    boxShadow: "0 1.592px 3.185px 1.194px rgba(63, 81, 181, 0.12)",
  },
  expandedCitationsGroup: {
    border: `2px solid ${
      theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0"
    }`,
    margin: -1,
    background:
      theme.palette.mode === "dark"
        ? theme.palette.primary.lightBg
        : "linear-gradient(0deg, rgba(156, 39, 176, 0.04) 0%, rgba(156, 39, 176, 0.04) 100%), #FFF",
    boxShadow: "0 1.592px 3.185px 1.194px rgba(63, 81, 181, 0.12)",
  },
  tagChip: {
    borderRadius: 4,
    // background: "#EEE",
    fontWeight: 500,
  },
  citation: {
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark" ? theme.palette.action.hover : "#F5F5F5",
    },
  },
  citationContent: {
    padding: "6px",
    marginLeft: "8px",
    marginRight: "8px",
    marginTop: "4px",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lastCitation: {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },

  generalDomain: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.light,
    padding: 12,
  },
}));

function SourcesList({ message, setSelectedSource, selectedSource }) {
  const answer = message["answer"];
  const citations = answer["content_object"]["citations"] || [];

  const handleSourceClick = (source) => {
    setSelectedSource({
      ...source,
      created_at: answer["created_at"],
    });
  };

  const groupedCitations = citations.reduce((acc, citation) => {
    const { file_id } = citation;
    if (!acc[file_id]) acc[file_id] = [];
    acc[file_id].push(citation);
    return acc;
  }, {});

  // Sort citations in each group by section name
  Object.keys(groupedCitations).forEach((file_id) => {
    groupedCitations[file_id].sort((a, b) => {
      return String(a.section).localeCompare(String(b.section));
    });
  });

  if (citations.length === 0) {
    return <GeneralDomain />;
  }

  const groupBySectionAndPage = (citationsInFile) => {
    const mergedMap = citationsInFile.reduce((acc, citation) => {
      const key = `${String(citation.section)}|${Number(citation.page)}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          section: citation.section,
          page: citation.page,
          file: citation.file,
          file_id: citation.file_id,
          citations: [],
        };
      }
      acc[key].citations.push(citation);
      return acc;
    }, {});

    // Build aggregated items with coordinate_list for multi-highlight
    return Object.values(mergedMap)
      .sort((a, b) => String(a.section).localeCompare(String(b.section)))
      .map((group) => {
        const first = group.citations[0];
        return {
          ...first,
          section: group.section,
          page: group.page,
          file: group.file,
          file_id: group.file_id,
          citations: group.citations,
          id: first.id, // keep first id for selection compatibility
          coordinate_list: group.citations
            .map((c) => c.coordinates)
            .filter((c) => typeof c === "string" && c.length > 0),
        };
      });
  };

  // Build sorted groups and compute base index for global numbering
  const sortedEntries = Object.entries(groupedCitations).sort(
    ([file_id_a], [file_id_b]) => {
      const fileA = groupedCitations[file_id_a][0]?.file;
      const fileB = groupedCitations[file_id_b][0]?.file;
      return String(fileA?.name || "").localeCompare(String(fileB?.name || ""));
    }
  );

  const groupsWithBaseIndex = (() => {
    let baseIndex = 0;
    const groups = sortedEntries.map(([_, group]) => {
      const merged = groupBySectionAndPage(group);
      const groupData = {
        file: group[0].file,
        mergedCitations: merged,
        rawCitations: group,
        baseIndex,
      };
      baseIndex += merged.length;
      return groupData;
    });
    return groups;
  })();

  // Build raw citation global index map (pre-merge) for label display
  const rawIndexById = (() => {
    let rawBase = 0;
    const map = {};
    sortedEntries.forEach(([_, rawGroup]) => {
      rawGroup.forEach((c, idx) => {
        map[c.id] = rawBase + idx + 1; // 1-based
      });
      rawBase += rawGroup.length;
    });
    return map;
  })();

  return (
    <Box display="flex" flexDirection="column">
      {groupsWithBaseIndex.map(
        ({ file, mergedCitations, rawCitations, baseIndex }) => (
          <CitationGroup
            key={file.id}
            file={file}
            mergedCitations={mergedCitations}
            rawCitations={rawCitations}
            selectedSource={selectedSource}
            handleSourceClick={handleSourceClick}
            baseIndex={baseIndex}
            rawIndexById={rawIndexById}
          />
        )
      )}
    </Box>
  );
}

function CitationGroup({
  file,
  mergedCitations,
  rawCitations,
  handleSourceClick,
  selectedSource,
  baseIndex,
  rawIndexById,
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { name, tags } = file;
  const tagsList = tags
    ? [tags.split(",")[0]].filter((tag) => tag && tag.trim() !== "")
    : [];
  const [isExpanded, setIsExpanded] = useState(true);
  const isSelectedGroup = (rawCitations || []).some(
    (citation) => selectedSource?.id === citation.id
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded && !isSelectedGroup) {
      if (mergedCitations && mergedCitations.length > 0) {
        handleSourceClick(mergedCitations[0]);
      }
    }
  };
  return (
    <Box
      my={2}
      className={`${classes.citationsGroup} ${
        isSelectedGroup ? classes.expandedCitationsGroup : ""
      }`}>
      <Box
        onClick={toggleExpanded}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ padding: "10px 12px 10px 20px", cursor: "pointer" }}>
        <Box display="flex" alignItems="center" gap={1}>
          <img
            src={getFileIconImage(file["file_type"])}
            alt={file["name"]}
            width={24}
            height={24}
          />
          <Typography>{name}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {tagsList.length > 0 && (
            <Box display={"flex"} gap={1} ml={1.5} flexShrink={0}>
              {tagsList.slice(0, 2).map((tag) => (
                <Chip
                  size="small"
                  key={tag}
                  label={tag[0].toUpperCase() + tag.slice(1)}
                  className={classes.tagChip}
                />
              ))}
            </Box>
          )}
          <IconButton size="small">
            {isExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
          </IconButton>
        </Box>
      </Box>
      {isExpanded && (
        <Fragment>
          <Divider sx={{ borderColor: theme.palette.divider }} />
          <Box>
            {mergedCitations.map((citation, index) => {
              const isSelectedCitation = (citation.citations || []).some(
                (c) => selectedSource?.id === c.id
              );
              return (
                <Fragment key={citation.id}>
                  <Box
                    className={`${classes.citation} ${
                      index === mergedCitations.length - 1
                        ? classes.lastCitation
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSourceClick(citation);
                    }}>
                    <Box
                      className={classes.citationContent}
                      sx={{
                        backgroundColor: isSelectedCitation
                          ? theme.palette.mode === "dark"
                            ? theme.palette.background.light
                            : "#F3E5F5"
                          : "transparent",
                        borderRadius: isSelectedCitation ? "8px" : "0px",
                        "&:hover": {
                          backgroundColor: isSelectedCitation
                            ? theme.palette.mode === "dark"
                              ? theme.palette.action.hover
                              : "#F3E5F5 !important"
                            : theme.palette.mode === "dark"
                            ? theme.palette.action.hover
                            : "#F5F5F5",
                        },
                      }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ paddingLeft: "16px", flexGrow: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            marginRight: 1,
                            minWidth: "28px",
                          }}>
                          {baseIndex + index + 1}.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2">
                            {citation.section}
                          </Typography>
                          {(() => {
                            const indices = (citation.citations || [])
                              .map((c) => rawIndexById?.[c.id])
                              .filter((n) => typeof n === "number")
                              .sort((a, b) => a - b);
                            const display =
                              indices.length > 0
                                ? indices
                                : [baseIndex + index + 1];
                            return (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mt: 0.25 }}>
                                Citations:{" "}
                                {display.map((n, i) => `[${n}]`).join(", ")}
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{ flexShrink: 0 }}>
                        <Typography variant="body2">
                          <span style={{ color: theme.palette.text.secondary }}>
                            Pg:{" "}
                          </span>
                          {citation.page <= 0 ? 1 : citation.page}
                        </Typography>
                        <IconButton size="small">
                          <EastRounded />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                  {index < mergedCitations.length - 1 && (
                    <Divider
                      sx={{
                        borderStyle: "dashed",
                        borderColor: theme.palette.divider,
                        marginLeft: "8px",
                        marginRight: "8px",
                      }}
                    />
                  )}
                </Fragment>
              );
            })}
          </Box>
        </Fragment>
      )}
    </Box>
  );
}

export function GeneralDomain() {
  const { classes } = useStyles();
  const theme = useTheme();
  return (
    <Box className={classes.generalDomain}>
      <Box display={"flex"} alignItems={"center"} gap={1}>
        <PublicRounded sx={{ color: theme.palette.action.active }} />
        <Typography>Answered from General Domain</Typography>
      </Box>
      <Box pl={4} pt={1}>
        <Typography variant="body2" color={"textSecondary"}>
          This information is generated from a general domain and may not be
          verified with trusted sources.
        </Typography>
      </Box>
    </Box>
  );
}

export default SourcesList;
